/**
 * MediaPageView — Content-type view for media_page.
 *
 * This is a thin wrapper around Volto's default block-based rendering that:
 *   1. Owns the content freshness engine (useContentFreshness) and processing
 *      trigger detection (useProcessingTrigger) — the single place that fetches
 *      enriched content with metadata_fields (output_state, subtitle_*, etc.)
 *   2. Dispatches enrichedContent to Redux so all consumers (VideoJS block,
 *      Downloads block, SubtitleModal in the portal-mounted Toolbar) can read
 *      it via useSelector without independent fetches or @workflow calls.
 *   3. Provides a place for future page-level UI that doesn't belong in a block.
 *
 * All existing blocks render unchanged via RenderBlocks.
 */

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Container as SemanticContainer } from 'semantic-ui-react';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { getBaseUrl, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import config from '@plone/volto/registry';

import { useContentFreshness } from 'volto-base-addon/components/manage/Blocks/VideoJS/useContentFreshness';
import { useProcessingTrigger } from 'volto-base-addon/components/manage/Blocks/VideoJS/useProcessingTrigger';
import {
  setMediaPageEnriched,
  clearMediaPageEnriched,
  setMediaPagePolling,
} from 'volto-base-addon/reducers/mediaPageEnriched';

const MediaPageView = (props) => {
  const { content, location } = props;
  const path = getBaseUrl(location?.pathname || '');
  const dispatch = useDispatch();

  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : null;
  const uid = content?.UID;

  // Layer 0: Trigger detection (URL param or Redux generation signal)
  const processingTrigger = useProcessingTrigger(uid);

  // Layer 1: Content freshness engine — fetches the page enriched with
  // output_state + subtitle metadata. Polls while a trigger is active.
  const { enrichedContent } = useContentFreshness(contentPath, {
    trigger: processingTrigger,
  });

  // Dispatch enriched content to Redux for cross-portal consumers
  useEffect(() => {
    if (enrichedContent) {
      dispatch(setMediaPageEnriched(enrichedContent));
    }
  }, [enrichedContent, dispatch]);

  // Track polling state
  useEffect(() => {
    dispatch(setMediaPagePolling(!!processingTrigger));
  }, [processingTrigger, dispatch]);

  // Clear enriched content on unmount (navigating away from media page)
  useEffect(() => {
    return () => {
      dispatch(clearMediaPageEnriched());
    };
  }, [dispatch]);

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  return hasBlocksData(content) ? (
    <Container id="page-document">
      <RenderBlocks {...props} path={path} />
    </Container>
  ) : (
    <Container id="page-document" />
  );
};

export default MediaPageView;
